import { Component, OnInit, ViewChild } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { Permissao } from '@models/enums/permissao';
import { MovimentacaoCaixaApiService } from '../services/movimentacao-caixa-api.service';
import { DiaCaixaInfo } from '../models/dia-caixa-info';
import { TipoLancamento } from '@models/tipo-lancamento';
import { ActivatedRoute, Data, Params } from '@angular/router';
import { LancamentoDiaCaixa } from '../models/lancamento-dia-caixa';
import { LancamentoEditCaixa } from '../models/lancamento-edit-caixa';
import { MessageService } from '@services/message.service';
import { Table, TableRowSelectEvent } from 'primeng/table';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoteLancamentoCaixa } from '../models/lote-lancamento-caixa';
import { LoteLancamentoEditCaixa } from '../models/lote-lancamento-edit-caixa';
import { MovimentacaoCaixaInfo } from '../models/movimentacao-caixa-info';
import { MoverLancamentoDialogComponent } from './dialogs/mover-lancamento-dialog/mover-lancamento-dialog.component';
import { CadastroLoteLancamentosDialogComponent, CadastroLoteLancamentosDialogData } from '../../../../shared/dialogs/cadastro-lote-lancamentos-dialog/cadastro-lote-lancamentos-dialog.component';

interface EdicaoLancamentoState {
  lancamento: LancamentoDiaCaixa;
  clone: LancamentoDiaCaixa;
}

interface EdicaoLoteState {
  lote: LoteLancamentoCaixa;
  clone: LoteLancamentoCaixa;
}

@Component({
  selector: 'app-movimentacao-caixa',
  standalone: false,
  templateUrl: './movimentacao-caixa.component.html',
  styleUrl: './movimentacao-caixa.component.scss'
})
export class MovimentacaoCaixaComponent implements OnInit {

  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }

  title: string = 'Movimentação do Caixa';
  dataMin: Date;
  dataMax: Date;

  movimentacaoCaixaInfo: MovimentacaoCaixaInfo;
  diaExibido: DiaCaixaInfo | undefined;
  diaSelecionado: DiaCaixaInfo | undefined;

  tiposLancamento: TipoLancamento[] = [];
  filteredTiposLancamento: TipoLancamento[][] = [];

  expandedRows = {};
  pathEditLote: string[] = ['/lote-lancamentos'];

  @ViewChild('tabelaLotes') tabelaLotes!: Table;
  lotesEmEdicao: Map<number, EdicaoLoteState> = new Map<number, EdicaoLoteState>();
  @ViewChild('tabelaLancamentos') tabelaLancamentos!: Table;
  lancamentosEmEdicao: Map<number, EdicaoLancamentoState> = new Map<number, EdicaoLancamentoState>();

  constructor(
    private route: ActivatedRoute,
    private movimentacaoCaixaApiService: MovimentacaoCaixaApiService,
    private messageService: MessageService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe((data: Data) => {
      const dados = data['tiposLancamento'];
      this.tiposLancamento = dados;
    });
    const dataNavegacao = this.obterDataDaUrl();
    this.setInitialDates(dataNavegacao);
    this.realizarBusca().then(() => this.selecionarDiaPorData(dataNavegacao));
  }

  private setInitialDates(dataBase: Date): void {
    this.dataMin = new Date(dataBase);
    this.dataMin.setDate(dataBase.getDate() - 31);

    this.dataMax = new Date(dataBase);
    this.dataMax.setDate(dataBase.getDate() + 31);
  }

  private obterDataDaUrl(): Date {
    const dataParam = this.route.snapshot.queryParamMap.get('data');
    if (!dataParam) return new Date();
    const [ano, mes, dia] = dataParam.split('-').map(Number);
    if (!ano || !mes || !dia) return new Date();
    return new Date(ano, mes - 1, dia);
  }

  private formatarDataParaUrl(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  public buscarClick(): void {
    if (this.podeMudarSelecao()) {
      this.realizarBusca();
    }
    else {
      const dialogRef = this.verificaAntesAlterarDiaSelecionado("Realizar busca");
      dialogRef.componentInstance.acaoConfirmacao = this.realizarBusca.bind(this);
    }
  }

  private async realizarBusca(diaExibidoId: string | undefined = undefined): Promise<void> {
    const dataMin = this.formatarDataParaUrl(this.dataMin);
    const dataMax = this.formatarDataParaUrl(this.dataMax);

    this.cancelaEdicoesInline();

    const info = await firstValueFrom(this.movimentacaoCaixaApiService.buscarLancamentosNoPeriodo(dataMin, dataMax));
    this.movimentacaoCaixaInfo = info;
    const diaExibido = info.diasCaixa.find(x => x.id === diaExibidoId);
    this.atualizarDiaExibido(diaExibido);
  }

  private selecionarDiaPorData(data: Date): void {
    const dataStr = this.formatarDataParaUrl(data);
    const selecao = this.movimentacaoCaixaInfo?.diasCaixa.find(x => x.data?.substring(0, 10) === dataStr);
    this.atualizarDiaExibido(selecao);
  }

  public abrirDialogAdicionarLote(): void {
    const dialogRef = this.dialog.open(CadastroLoteLancamentosDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: { data: this.diaExibido?.data ? new Date(this.diaExibido.data) : null } as CadastroLoteLancamentosDialogData,
    });
    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) this.realizarBusca(this.diaExibido?.id);
    });
  }

  public onRowEditLoteInit(lote: any) {
    this.lotesEmEdicao.set(lote.id, {
      lote: lote,
      clone: { ...lote }
    });
  }

  public onRowEditLoteSave(lote: LoteLancamentoCaixa): void {
    const loteEdit: LoteLancamentoEditCaixa = {
      id: lote.id,
      nome: lote.nome
    };

    this.movimentacaoCaixaApiService.editLote(loteEdit).subscribe({
      next: (a: LoteLancamentoEditCaixa) => {
        this.messageService.showToastSuccess("Salvo com sucesso.");

        this.lotesEmEdicao.delete(lote.id);

        lote.id = a.id;
        lote.nome = a.nome;
      },
      error: (err) => {
        const loteClone = this.lotesEmEdicao.get(lote.id);
        lote.nome = loteClone!.clone.nome;
        this.lotesEmEdicao.delete(lote.id);
      }
    });
  }

  public onRowEditLoteCancel(lote: LoteLancamentoCaixa): void {
    const loteAntes = this.lotesEmEdicao.get(lote.id)!.clone;
    lote.nome = loteAntes.nome;

    this.tabelaLotes.cancelRowEdit(lote);
    this.lotesEmEdicao.delete(lote.id);
  }

  public onRowEditLancamentoInit(lancamento: any) {
    this.lancamentosEmEdicao.set(lancamento.id, {
      lancamento: lancamento,
      clone: { ...lancamento }
    });
  }

  public onRowEditLancamentoSave(lancamento: LancamentoDiaCaixa): void {
    const lancamentoEdit: LancamentoEditCaixa = {
      id: lancamento.id,
      tipoLancamentoId: lancamento.tipoLancamento.id,
      valor: lancamento.valor
    };

    const diaExibidoId = this.diaExibido?.id;

    this.movimentacaoCaixaApiService.patchLancamento(lancamentoEdit).subscribe(() => {
      this.messageService.showToastSuccess("Salvo com sucesso.");
      this.lancamentosEmEdicao.delete(lancamento.id);
      this.realizarBusca(diaExibidoId);
    });
  }

  public onRowEditLancamentoCancel(lancamento: any): void {
    const lancamentoAntes = this.lancamentosEmEdicao.get(lancamento.id)!.clone;
    lancamento.valor = lancamentoAntes.valor;
    lancamento.tipoLancamento = lancamentoAntes.tipoLancamento;

    this.tabelaLancamentos.cancelRowEdit(lancamento);
    this.lancamentosEmEdicao.delete(lancamento.id);
  }

  public onMoverLancamentoDeLoteClick(lancamento: LancamentoDiaCaixa, lote: LoteLancamentoCaixa) {
    const dialogRef = this.dialog.open(MoverLancamentoDialogComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: { lancamentoIds: [lancamento.id], data: new Date(lote.data) }
    });
    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) this.realizarBusca(this.diaExibido?.id);
    });
  }

  public onDeleteLancamentoClick(lancamento: any) {
    if (!this.podeMudarSelecao()) {
      const dialogRefVerificacao = this.verificaAntesAlterarDiaSelecionado("A exclusão irá atualizar a pagina");
      dialogRefVerificacao.componentInstance.acaoConfirmacao = () => this.openDialogCheckDelete(lancamento);
    }
    else {
      this.openDialogCheckDelete(lancamento);
    }
  }

  private openDialogCheckDelete(lancamento: any): void {

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      autoFocus: false
    });
    dialogRef.componentInstance.titulo = "Excluir lançamento";
    dialogRef.componentInstance.mensagem = `Essa ação não pode ser desfeita`;
    dialogRef.componentInstance.mensagemBold = 'Deseja continuar?';
    dialogRef.componentInstance.acaoConfirmacao = this.excluirLancamento.bind(this, lancamento.id);
    dialogRef.componentInstance.textoBtnConfirmacao = "Excluir";
    dialogRef.componentInstance.btnBackgroundColor = "#F44336";
  }

  private excluirLancamento(lancamentoId: number): void {
    const diaExibidoId = this.diaExibido?.id;

    this.movimentacaoCaixaApiService.deleteLancamento(lancamentoId).subscribe(() => {
      this.messageService.showToastSuccess("Excluido com sucesso.");
      this.realizarBusca(diaExibidoId);
    })
  }


  public onDiaSelect(event: TableRowSelectEvent<DiaCaixaInfo>): void {
    const novoDia = event.data as DiaCaixaInfo | undefined;

    if (!this.podeMudarSelecao()) {
      const dialogRef = this.verificaAntesAlterarDiaSelecionado("Alterar dia selecionado");
      dialogRef.componentInstance.acaoConfirmacao = this.alterarDiaSelecionadoCancelandoEdicoes.bind(this, novoDia);
      return;
    }
    else {
      this.atualizarDiaExibido(novoDia);
    }
  }

  private verificaAntesAlterarDiaSelecionado(titulo: string): MatDialogRef<DialogConfirmacaoComponent, any> {
    setTimeout(() => {
      this.diaSelecionado = this.diaExibido;
    }, 0);

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      autoFocus: false
    });
    dialogRef.componentInstance.titulo = titulo;
    dialogRef.componentInstance.mensagem = `Existem edições de lançamento em andamento.`;
    dialogRef.componentInstance.mensagemBold = 'Deseja descartar as edições e prosseguir?';
    return dialogRef;
  }

  private podeMudarSelecao(): boolean {
    return this.lancamentosEmEdicao.size === 0 && this.lotesEmEdicao.size === 0;
  }

  private cancelaEdicoesInline(): void {
    for (const state of this.lancamentosEmEdicao.values()) {
      this.onRowEditLancamentoCancel(state.lancamento);
    }

    for (const state of this.lotesEmEdicao.values()) {
      this.onRowEditLoteCancel(state.lote);
    }
  }

  private alterarDiaSelecionadoCancelandoEdicoes(novoDia: DiaCaixaInfo | undefined): void {
    this.cancelaEdicoesInline();
    this.atualizarDiaExibido(novoDia);
  }

  private atualizarDiaExibido(novoDia: DiaCaixaInfo | undefined): void {
    this.diaSelecionado = novoDia;
    this.diaExibido = novoDia;
  }


  public getQueryLote(lote: LoteLancamentoCaixa): Params {
    return { nome: lote.nome, data: lote.data };
  }
}
